import React, { useCallback, useEffect, useState } from 'react'
import { Video as VideoIcon, Loader2, AlertCircle, Trash2, Pencil, Plus, X, Eye, EyeOff } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import {
  listVideosAdmin,
  getVideoStats,
  createVideo,
  updateVideo,
  changeVideoStatus,
  deleteVideo,
  VIDEO_CATEGORY_KEYS,
  VIDEO_CATEGORY_LABELS,
  VIDEO_LANGUAGE_LABELS,
  VIDEO_STATUS_LABELS,
  type Video,
  type VideoRequest,
  type VideoCategory,
  type ResourceStatus,
} from '@/services/videoService'

const STATUS_FILTERS: (ResourceStatus | 'ALL')[] = ['ALL', 'DRAFT', 'PUBLISHED', 'ARCHIVED']
const STATUS_LIST: ResourceStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED']
const LANGS = Object.keys(VIDEO_LANGUAGE_LABELS)

const statusTone: Record<ResourceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-amber-100 text-amber-700',
}

const EMPTY_FORM: VideoRequest = {
  title: '', slug: '', description: '', category: 'TB_AWARENESS',
  youtubeId: '', externalUrl: '', thumbnailUrl: '', duration: '', language: 'hi',
  sourceOrg: '', isFeatured: false, status: 'DRAFT',
}

const VideosManagement = React.memo(() => {
  const [items, setItems] = useState<Video[]>([])
  const [stats, setStats] = useState<{ TOTAL: number; DRAFT: number; PUBLISHED: number; ARCHIVED: number }>({ TOTAL: 0, DRAFT: 0, PUBLISHED: 0, ARCHIVED: 0 })
  const [statusFilter, setStatusFilter] = useState<ResourceStatus | 'ALL'>('ALL')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<Video | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<VideoRequest>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const size = 20

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, s] = await Promise.all([
        listVideosAdmin({ status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined, page, size }),
        getVideoStats(),
      ])
      setItems(data.content); setTotalPages(data.totalPages); setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load videos.')
    } finally { setLoading(false) }
  }, [statusFilter, q, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter, q])

  const flash = (tone: 'success' | 'error', msg: string) => { setToast({ tone, msg }); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setShowForm(true) }
  const openEdit = (v: Video) => {
    setEditing(v)
    setForm({
      title: v.title, slug: v.slug, description: v.description ?? '', category: v.category,
      youtubeId: v.youtubeId ?? '', externalUrl: v.externalUrl ?? '', thumbnailUrl: v.thumbnailUrl ?? '',
      duration: v.duration ?? '', language: v.language ?? '', sourceOrg: v.sourceOrg ?? '',
      isFeatured: v.isFeatured, status: v.status,
    })
    setFormError(null); setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setFormError(null) }

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!form.title.trim()) { setFormError('Title is required.'); return }
    if (!form.category) { setFormError('Category is required.'); return }
    setSaving(true); setFormError(null)
    try {
      const payload: VideoRequest = { ...form, title: form.title.trim(), isFeatured: Boolean(form.isFeatured) }
      if (editing) {
        await updateVideo(editing.id, payload)
        flash('success', `Updated “${form.title}”`)
      } else {
        await createVideo(payload)
        flash('success', `Created “${form.title}”`)
      }
      closeForm(); await load()
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'Failed to save.')
    } finally { setSaving(false) }
  }

  const changeStatus = async (v: Video, status: ResourceStatus) => {
    setActingId(v.id)
    try { await changeVideoStatus(v.id, status); flash('success', `“${v.title}” → ${VIDEO_STATUS_LABELS[status]}`); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  const remove = async (v: Video) => {
    if (!window.confirm(`Delete “${v.title}”? This cannot be undone.`)) return
    setActingId(v.id)
    try { await deleteVideo(v.id); flash('success', 'Video deleted'); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="Video Library" subtitle="Manage TB awareness videos — link to verified official Central TB Division / Ni-kshay sources. Do not fabricate government video IDs." icon={VideoIcon} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-500">{stats.DRAFT}</p><p className="text-sm text-gray-500">Draft</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.PUBLISHED}</p><p className="text-sm text-gray-500">Published</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.ARCHIVED}</p><p className="text-sm text-gray-500">Archived</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : VIDEO_STATUS_LABELS[f]}
              </button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title/description…" className="text-sm border border-gray-200 rounded-md px-3 py-1.5 ml-auto" />
            <GovButton variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Add video</GovButton>
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Videos</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : items.length === 0 ? (
            <GovEmptyState icon="🎬" title="No videos" description="Add your first video with a verified official source, or adjust the filter/search." />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((v) => (
                <div key={v.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[v.status]}`}>{VIDEO_STATUS_LABELS[v.status]}</span>
                        {v.isFeatured && <span className="text-xs text-teal-600 font-medium">★ Featured</span>}
                        <span className="text-xs text-gray-400">{VIDEO_CATEGORY_LABELS[v.category]}</span>
                      </div>
                      <h4 className="font-medium text-gray-900">{v.title}</h4>
                      {v.description && <p className="text-sm text-gray-600 line-clamp-2 mt-1">{v.description}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {v.youtubeId ? `YouTube: ${v.youtubeId}` : 'No YouTube id'}
                        {v.language && ` · ${VIDEO_LANGUAGE_LABELS[v.language] ?? v.language}`}
                        {v.sourceOrg && ` · ${v.sourceOrg}`}
                      </p>
                      {v.externalUrl && (
                        <a href={v.externalUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block truncate max-w-full">
                          {v.externalUrl}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === v.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      {v.status === 'PUBLISHED'
                        ? <GovButton variant="ghost" size="sm" onClick={() => changeStatus(v, 'ARCHIVED')} icon={<EyeOff className="w-3.5 h-3.5" />}>Archive</GovButton>
                        : <GovButton variant="ghost" size="sm" onClick={() => changeStatus(v, 'PUBLISHED')} icon={<Eye className="w-3.5 h-3.5" />}>Publish</GovButton>}
                      <button onClick={() => openEdit(v)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(v)} disabled={actingId === v.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={closeForm}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editing ? 'Edit video' : 'Add video'}</h3>
                  <p className="text-sm text-gray-500 mt-1">Only add videos with a verified official source (e.g. Central TB Division YouTube/Drive). Do not fabricate government video IDs.</p>
                </div>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>

              {formError && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {formError}</div>}

              <form onSubmit={save} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-title">Title <span className="text-red-500">*</span></label>
                  <input id="v-title" type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-slug">Slug (optional)</label>
                    <input id="v-slug" type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input" placeholder="auto from title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-cat">Category <span className="text-red-500">*</span></label>
                    <select id="v-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as VideoCategory })} className="select">
                      {VIDEO_CATEGORY_KEYS.map((c) => <option key={c} value={c}>{VIDEO_CATEGORY_LABELS[c]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-desc">Description</label>
                  <textarea id="v-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-yt">YouTube video id</label>
                    <input id="v-yt" type="text" value={form.youtubeId ?? ''} onChange={(e) => setForm({ ...form, youtubeId: e.target.value || null })} className="input" placeholder="e.g. mEughC7afJs" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-ext">External URL (official source)</label>
                    <input id="v-ext" type="url" value={form.externalUrl ?? ''} onChange={(e) => setForm({ ...form, externalUrl: e.target.value || null })} className="input" placeholder="https://…" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-thumb">Thumbnail URL</label>
                    <input id="v-thumb" type="text" value={form.thumbnailUrl ?? ''} onChange={(e) => setForm({ ...form, thumbnailUrl: e.target.value || null })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-dur">Duration</label>
                    <input id="v-dur" type="text" value={form.duration ?? ''} onChange={(e) => setForm({ ...form, duration: e.target.value || null })} className="input" placeholder="2:30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-lang">Language</label>
                    <select id="v-lang" value={form.language ?? ''} onChange={(e) => setForm({ ...form, language: e.target.value || null })} className="select">
                      <option value="">—</option>
                      {LANGS.map((l) => <option key={l} value={l}>{VIDEO_LANGUAGE_LABELS[l]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-src">Source / publisher</label>
                    <input id="v-src" type="text" value={form.sourceOrg ?? ''} onChange={(e) => setForm({ ...form, sourceOrg: e.target.value })} className="input" placeholder="e.g. Central TB Division, MoHFW" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="v-status">Status</label>
                    <select id="v-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ResourceStatus })} className="select">
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{VIDEO_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={Boolean(form.isFeatured)} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} />
                  Featured video
                </label>

                <div className="flex items-center gap-3 pt-2">
                  <GovButton type="submit" variant="primary" disabled={saving} icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>{saving ? 'Saving…' : 'Save video'}</GovButton>
                  <GovButton variant="ghost" onClick={closeForm}>Cancel</GovButton>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </GovLayout>
  )
})

VideosManagement.displayName = 'VideosManagement'
export default VideosManagement